import { v4 as uuid } from 'uuid';
import { forkJoin, mergeMap, of } from "rxjs";
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatStates from '@medsurf/flat-states';
import * as FlatFacades from '../../internal';

@Injectable()
export class LayerEntityFacade {
  constructor(protected store: Store) {
  }

  //<editor-fold desc="Selectors">

  //</editor-fold>

  //<editor-fold desc="Requests">
  
  static addLayer(store: Store,
                  page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training) {
    // Create New Layer
    const newLayer: FlatModels.LayerEntityModels.Layer = {
      id: uuid(),
      position: page.layers?.length || 0,
      images: [],
      default: false,
      window: null,
      slide: page.id
    };
    
    // Update Page
    page.layers.push(newLayer.id);
    
    // Add Layer
    return store.dispatch(new FlatActions.SlideLayerEntityActions.AddEntity(newLayer)).pipe(
      mergeMap(() =>
        store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page))
      ),
      mergeMap((data) => {
        store.dispatch(new FlatActions.SlideAuthorActions.AddTypedSlideLayersExpanded(page.id));
        store.dispatch(new FlatActions.SlideLayerEntityActions.SetLayerIndex(page.layers.length - 1));
        store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.layer));
        store.dispatch(new FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView(newLayer.id));
        return of(data);
      }),
    );
  }
  
  protected static copyLayer(store: Store,
                             mappings: FlatModels.BulkEditAuthorModels.BulkEditMapping,
                             id: string,
                             page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                             withPois: boolean,
                             withAnnotations: boolean,
                             clusterParentUpdate = false) {
    // Check If Layer Mapping Exits
    if (mappings.layer[id]) {
      return of(undefined);
    }
    
    // Get Data
    const typedLayers = store.selectSnapshot(FlatStates.SlideLayerEntityState.typedEntities$);
    
    // Get Layer
    const layer = structuredClone(typedLayers[id]);
    
    // Update Layer Mappings
    mappings.layer[id] = uuid();
    
    // Update Layer
    layer.id = mappings.layer[id];
    layer.slide = page.id;
    
    // Update Page
    page.layers.push(layer.id);
    
    // Copy Layer
    return of(layer.id).pipe(
      mergeMap(() => {
        const images = structuredClone(layer.images);
        layer.images = [];
        return FlatFacades.ImageEntityFacade.copyImages(store, mappings, images, page, layer, withPois, withAnnotations, true)
      }),
      mergeMap(() =>
        store.dispatch(new FlatActions.SlideLayerEntityActions.AddEntity(layer))
      ),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of(data);
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page));
      }),
      mergeMap(() => of(layer.id))
    );
  }
  
  static copyLayers(store: Store,
                    mappings: FlatModels.BulkEditAuthorModels.BulkEditMapping,
                    ids: string[],
                    page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                    withPois: boolean,
                    withAnnotations: boolean,
                    clusterParentUpdate = false) {
    if (ids.length === 0) {
      return of([]);
    }
    
    // Copy Layers
    return forkJoin(ids.map(id => LayerEntityFacade.copyLayer(store, mappings, id, page, withPois, withAnnotations, true))).pipe(
      mergeMap((newIds) => {
        const selectIds = newIds.reduce((arr, id) => {
          if (id !== undefined) {
            arr.push(id);
          }
          return arr;
        }, [] as string[]);
        return of(selectIds);
      }),
      mergeMap((selectIds) => {
        if (clusterParentUpdate) {
          return of(selectIds);
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page)).pipe(
          mergeMap(() => of(selectIds))
        );
      }),
    );
  }

  static removeLayer(store: Store,
                     id: string,
                     clusterParentUpdate = false) {
    // Get Data
    const typedLayers = store.selectSnapshot(FlatStates.SlideLayerEntityState.typedEntities$);

    // Get Layer
    const layer = structuredClone(typedLayers[id]);
    
    // Create Layer Pipe Object
    const layerPipeModel: FlatModels.PipeEntityModels.LayerPipeObject = {
      data: {},
      layer,
    };
    
    // Get Remove Pois And Annotations
    const removePois: string[] = [];
    const removeAnnotations: string[] = []

    // Remove Layer
    return of(layerPipeModel).pipe(
      mergeMap((result) => {
        return store.dispatch(new FlatActions.BulkEditAuthorActions.RemoveLayer(id)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data}))),
        )
      }),
      mergeMap((result) => {
        if (layer.images.length > 0) {
          return forkJoin(layer.images.map(image => FlatFacades.ImageEntityFacade.removeImage(store, image, true))).pipe(
            mergeMap((data: FlatModels.PipeEntityModels.ImagePipeObject[]) => {
              data.forEach((imagePipeObject) => {
                if (imagePipeObject.pois) {
                  imagePipeObject.pois.forEach((poi) => {
                    removePois.push(poi.poi.id);
                  })
                }
                if (imagePipeObject.annotations) {
                  imagePipeObject.annotations.forEach((annotation) => {
                    removeAnnotations.push(annotation.annotation.id);
                  })
                }
              });
              return of(data);
            }),
          )
        }
        return of(result);
      }),
      mergeMap(() =>
        store.dispatch(new FlatActions.SlideLayerEntityActions.RemoveEntity(layer))
      ),
      mergeMap((data) => {
        // Get Page
        const typedPages = store.selectSnapshot(FlatStates.PageEntityState.typedEntities$);
        const page = structuredClone(typedPages[layer.slide]) as FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training;
        // Remove Layer
        page.layers.splice(page.layers.findIndex(id => id === layer.id), 1);
        // Remove Pois
        page.pois = page.pois.filter(a => removePois.indexOf(a) === -1);
        // Remove Annotations
        page.annotations = page.annotations.filter(a => removeAnnotations.indexOf(a) === -1);
        if (clusterParentUpdate) {
          return of({data, page});
        }
        return store.dispatch(new FlatActions.SlideLayerEntityActions.RearrangeLayers(page.layers)).pipe(
          mergeMap(() =>
            store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page))
          )
        )
      }),
    );
  }

  static removeLayers(store: Store,
                      ids: string[]) {
    if (ids.length === 0) {
      return of([]);
    }

    // Remove Layers
    return of({}).pipe(
      mergeMap(() =>
        forkJoin(ids.map(id => LayerEntityFacade.removeLayer(store, id, true)))
      ),
      mergeMap((data: {data: Store, page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training}[]) => {
        const pageMap = data.reduce((arr, clusterParentUpdate) => {
          const page = structuredClone(arr.get(clusterParentUpdate.page.id));
          if (page) {
            page.layers = clusterParentUpdate.page.layers.filter((id: string) => page.layers.includes(id));
            arr.set(clusterParentUpdate.page.id, page);
          } else {
            arr.set(clusterParentUpdate.page.id, clusterParentUpdate.page);
          }
          return arr;
        }, new Map<string, FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training>());
        const pages = Array.from(pageMap.values());
        if (pages.length > 0) {
          return forkJoin(pages.map((page) =>
            store.dispatch(new FlatActions.SlideLayerEntityActions.RearrangeLayers(page.layers)).pipe(
              mergeMap(() =>
                store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page))
              )
            )
          ))
        }
        return of(data);
      }),
    );
  }
  
  static removeImageFromLayer(store: Store,
                              id: string,
                              layerId: string,
                              clusterParentUpdate = false) {
    const typedLayers = store.selectSnapshot(FlatStates.SlideLayerEntityState.typedEntities$);
    const layer = structuredClone(typedLayers[layerId]);
    
    // Check Index
    const index = layer.images.findIndex((i: string) => i === id);
    if (index < 0) {
      return of({});
    }
    
    // Update Layer
    layer.images.splice(index, 1);
    
    // Remove Image From Layer
    return of({layer}).pipe(
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data});
        }
        return store.dispatch(new FlatActions.SlideLayerEntityActions.UpdateEntity(layer))
      }),
    );
  }
  
  static removeImagesFromLayer(store: Store,
                               ids: string[],
                               layerId: string,
                               clusterParentUpdate = false) {
    if (ids.length === 0) {
      return of({layerId});
    }
    
    const typedLayers = store.selectSnapshot(FlatStates.SlideLayerEntityState.typedEntities$);
    const layer = structuredClone(typedLayers[layerId]);
    
    for (const id of ids) {
      // Check Index
      const index = layer.images.findIndex((i: string) => i === id);
      if (index > -1) {
        // Update Layer
        layer.images.splice(index, 1);
      }
    }
    
    // Remove Images From Layer
    return of({layer}).pipe(
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data});
        }
        return store.dispatch(new FlatActions.SlideLayerEntityActions.UpdateEntity(layer))
      }),
    );
  }

  //</editor-fold>
}
